import React, { Children, cloneElement } from 'react'
import { get, pick } from 'lodash-es'
import { FastField, FieldArray, Formik, FieldProps, FormikProps } from 'formik'
import * as yup from 'yup'
import CurrencyField from '../../../../views/components/molecules/CurrencyField/CurrencyField'
import FormField from '../../../../views/components/atoms/FormField/FormField'
import Select from '../../../../views/components/atoms/Select/Select'
import CurrencyText from '../../../../views/components/atoms/Typography/CurrencyText/CurrencyText'
import FormLoader from '../../../../views/components/molecules/FormLoader/FormLoader'
import styles from './CreditNoteForm.module.scss'
import { currencyUtils, formUtils } from '../../../../utils'
import { isEmpty } from 'ramda'
import { useSelector } from 'react-redux'
import { apiStateSelectors } from '../../../api/apiState'
import { reconApiEvents } from '../../../api/recon'
import { $TSFixMe } from 'types/ts-migrate'
import TextField from '../../../../views/components/atoms/TextField/TextField'
import Label from '../../../../views/components/atoms/Label/Label'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'

interface CreditNoteBeneficiary {
  beneficiaryId: string
  name: string
  reference: string
  balance: number
  amount?: number | string
}

interface CreditNoteReason {
  label: string
  value: string
}

interface CreateCreditNoteBody {
  amount: number | string
  reason: string
  description: string
  userUnderstandsRisk: boolean
  beneficiaries: CreditNoteBeneficiary[]
}

interface CreditNoteFormValues {
  amount: number | string
  reason: string
  description: string
  invoiceBalance: number
  beneficiaries: CreditNoteBeneficiary[]
  userUnderstandsRisk: boolean
}

interface CreditNoteFormProps {
  invoiceId: string
  invoiceBalance: number
  beneficiaries: CreditNoteBeneficiary[]
  creditNoteReasons: CreditNoteReason[]
  createCreditNote: (invoiceId: string, body: CreateCreditNoteBody) => void
  isSubmitting: boolean
}

const validationSchema = yup.object().shape({
  reason: yup.string().required('required.'),
  amount: yup
    .number()
    .required()
    .test('amount', function (val) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'parent' does not exist on type 'Validate... Remove this comment to see the full error message
      const { parent } = this.options
      const balance = parent.invoiceBalance
      return val === 0
        ? new yup.ValidationError(`required.`, val, 'amount')
        : // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
        val > balance
        ? new yup.ValidationError(
            `can't exceed invoice balance of ${currencyUtils.formatCurrency(balance)}`,
            val,
            'amount',
          )
        : true
    }),
  description: yup.string().test('description', function (description) {
    if (description && description.length > 256) {
      return new yup.ValidationError(`Can't exceed character limit of 256`, description, 'description')
    }
    return true
  }),
  beneficiaries: yup
    .array(
      yup.object().shape({
        beneficiaryId: yup.string(),
        name: yup.string(),
        reference: yup.string(),
        balance: yup.number(),
        amount: yup.mixed().test('amount', "Can't exceed balance", function (val) {
          const {
            // @ts-expect-error ts-migrate(2339) FIXME: Property 'parent' does not exist on type 'Validate... Remove this comment to see the full error message
            parent: { balance },
          } = this.options
          const amount = val === undefined ? 0 : parseFloat(val)
          if (amount > balance) {
            return new yup.ValidationError(
              `Can't exceed beneficiary balance of ${currencyUtils.formatCurrency(balance)}`,
              amount,
              // @ts-expect-error ts-migrate(2339) FIXME: Property 'path' does not exist on type 'ValidateOp... Remove this comment to see the full error message
              this.options.path,
            )
          }
          return true
        }),
      }),
    )
    .test('beneficiaries', '', function (val) {
      const {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'parent' does not exist on type 'Validate... Remove this comment to see the full error message
        parent: { amount },
      } = this.options
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      const beneficiariesTotal = val.reduce((acc, val) => {
        let amount = parseFloat(val.amount)
        if (amount === undefined || isNaN(amount)) {
          amount = 0
        }
        return acc + amount
      }, 0)
      return beneficiariesTotal > amount
        ? new yup.ValidationError(
            `Beneficiaries allocation can't exceed credit note amount of ${currencyUtils.formatCurrency(amount)}`,
            amount,
            // @ts-expect-error ts-migrate(2339) FIXME: Property 'path' does not exist on type 'ValidateOp... Remove this comment to see the full error message
            this.options.path,
          )
        : true
    }),
})

// eslint-disable-next-line react/display-name
const withButtonContainer =
  ({ submitForm }): $TSFixMe =>
  // eslint-disable-next-line react/display-name
  ({ children }: any): $TSFixMe => {
    // Overwrite child type='submit' prop to type='button'
    // Some strange behaviour where a submit button submits the payment form instead of credit note form :/
    return (
      <FormField>
        {Children.map(children, child => {
          return cloneElement(
            child,
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            null,
            Children.map(child.props.children, grandChild => {
              const props =
                grandChild.props?.type === 'submit'
                  ? {
                      type: 'button',
                      onMouseDown: (e: $TSFixMe) => {
                        e.preventDefault()
                      },
                      onClick: submitForm,
                    }
                  : null
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              return grandChild && cloneElement(grandChild, props)
            }),
          )
        })}
      </FormField>
    )
  }

const CreditNoteForm = ({
  invoiceId,
  creditNoteReasons,
  invoiceBalance,
  beneficiaries,
  createCreditNote,
  isSubmitting,
}: CreditNoteFormProps) => {
  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  return (
    <Formik<CreditNoteFormValues>
      validationSchema={validationSchema}
      validateOnMount={false}
      initialValues={{
        amount: '',
        reason: '',
        description: '',
        invoiceBalance: invoiceBalance,
        beneficiaries: beneficiaries,
        userUnderstandsRisk: true,
      }}
      onSubmit={values => {
        const data = pick(values, ['amount', 'reason', 'description', 'userUnderstandsRisk', 'beneficiaries'])
        createCreditNote(invoiceId, {
          ...data,
          beneficiaries: data.beneficiaries.map((b: CreditNoteBeneficiary) => ({
            ...b,
            amount: b.amount || 0,
          })),
        })
      }}
    >
      {({
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        handleChange,
        errors,
        touched,
        submitCount,
        submitForm,
      }: FormikProps<CreditNoteFormValues>) => {
        const event = reconApiEvents.creditNote_request
        const generalErrors = getGeneralFormErrorsByEvent(event)
        const fieldErrors = getFormFieldErrorsByEvent(event)
        const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

        // Only surface field errors once the field has been interacted with or a submit was attempted
        const getFieldError = formUtils.getFieldError(submitCount, touched, errors)

        const formLoaderState = isSubmitting
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
          ? 'error'
          : undefined

        return (
          <div className={styles.root}>
            <FormLoader
              state={formLoaderState}
              ButtonContainer={withButtonContainer({ submitForm })}
              buttonProps={{ children: 'Add credit note' }}
              onSubmit={handleSubmit}
              submittingText="Creating credit note..."
              errorText={hasApiErrors ? 'Oops, something went wrong' : 'Resolve errors'}
              persistErrorMessage={!hasApiErrors}
            >
              <>
                <div className={styles['required-fields-info']}>
                  Fields marked with <span>*</span> are required.
                </div>
                <div className="rbn--row">
                  <div className="rbn--col-sm-4 rbn--col-md-8 rbn--col-lg-8">
                    <FastField name="amount">
                      {({ field }: FieldProps) => (
                        <CurrencyField
                          {...field}
                          label="Amount"
                          required={true}
                          error={getFieldError('amount')}
                          onChange={(e: any) => {
                            // CurrencyField (react-number-format) also fires onChange for programmatic
                            // value changes on mount, so only mark the field touched for real input
                            if (e.target.value) {
                              setFieldTouched('amount', true)
                            }
                            setFieldValue('amount', e.target.value)
                          }}
                        />
                      )}
                    </FastField>
                    <div className={styles['balance-info']}>
                      <Label circular className={styles['balance-label']}>
                        Outstanding: <CurrencyText>{invoiceBalance ?? 0}</CurrencyText>
                      </Label>
                    </div>
                  </div>

                  <div className="rbn--col-sm-4 rbn--col-md-8 rbn--col-lg-8">
                    <FastField name="reason">
                      {({ field }: FieldProps) => (
                        <Select
                          {...field}
                          value={creditNoteReasons.find((r: CreditNoteReason) => r.value === field.value)}
                          label="Reason"
                          labelHint="This reason will appear on the account statement"
                          required={true}
                          error={getFieldError('reason')}
                          onChange={(data: any) => {
                            setFieldTouched('reason', true)
                            setFieldValue('reason', data.value)
                          }}
                          options={creditNoteReasons}
                        />
                      )}
                    </FastField>
                  </div>
                </div>

                <div className="rbn--row">
                  <div className="rbn--col">
                    <FastField name="description">
                      {({ field }: FieldProps) => (
                        <TextField
                          className={styles.description}
                          error={get(errors, 'description')}
                          label="Description"
                          labelHint="This description will appear on the PDF invoice and credit note"
                          inputComponent={
                            // @todo - Figure out a way to use <TextArea /> for this without getting an error
                            <textarea className={styles.description} {...field} onChange={handleChange} />
                          }
                        />
                      )}
                    </FastField>
                  </div>
                </div>
              </>
            </FormLoader>
          </div>
        )
      }}
    </Formik>
  )
}

export default CreditNoteForm
